import { useState } from 'react';
import { produce } from 'immer';
import { isEqual, sortBy, isEmpty } from 'lodash-es';
import {
  type RemoveIngredientMutation,
  type PairingMutation,
  MutationTypes,
  type AddIngredientMutation,
  type SwapMutation,
} from '@hungryroot/cart';
import {
  type PairingV1 as Pairing,
  type PairingBundle,
} from '@hungryroot/rest';
import { type OrderIngredientViewModel } from '../../components/Ingredient/Ingredient';
import { useSwappableRecipesQuery } from '../useSwappableRecipesQuery/useSwappableRecipes';

export type EditRecipeOrderDetail = Pick<
  PairingBundle['order_details'][number],
  'product'
>;

type EditRecipeBundle = {
  order_details: EditRecipeOrderDetail[];
  points: number | string;
};

export type Params = {
  initialPairing: Pairing;
  orderId?: number;
  initialBundle?: EditRecipeBundle;
  inventoryDate?: string;
};

export interface PairingWithIngredientDetails
  extends Omit<Pairing, 'ingredients'> {
  ingredients: OrderIngredientViewModel[];
}

export interface UseEditRecipeResult {
  // This is the original, pristene list of ingredient IDs
  pairing: PairingWithIngredientDetails;
  bundle: EditRecipeBundle;

  // APIs for removing and add ingredients.
  removeIngredient?: (
    ingredientId: number,
    reasonId: number,
    onUpdate?: (mutations: PairingMutation[]) => void
  ) => void;
  addIngredient?: (
    ingredientId: number,
    onUpdate?: (mutations: PairingMutation[]) => void
  ) => void;

  // Data for swapping pairing.
  // pairings is a key/value store where KEY is the ingredient ID
  swap?: {
    swapPairing: (
      pairing: Pairing,
      onUpdate?: (mutations: PairingMutation[], pairing: Pairing) => void
    ) => void;
    pairings: Record<string, Pairing[]>;
  };

  mutations: PairingMutation[];
  resetProductsToRemove?: () => void;
  isEdited: boolean;
}

interface UseEditRecipeState {
  bundle: EditRecipeBundle;
  pairing: Pairing;
  mutations: PairingMutation[];
}

function createOrderDetails(pairing: Pairing): EditRecipeOrderDetail[] {
  const { ingredients: pairingIngredients } = pairing;
  const orderDetails: EditRecipeOrderDetail[] = [];

  pairingIngredients.forEach(({ id, amount }) => {
    for (let i = 0; i < amount; i += 1) {
      orderDetails.push({
        product: id,
      });
    }
  });

  return orderDetails;
}

export function useEditRecipe(params: Params): UseEditRecipeResult {
  const {
    initialPairing,
    inventoryDate,
    orderId,
    initialBundle: initialBundleParam,
  } = params;
  const initialBundle: EditRecipeBundle =
    initialBundleParam !== undefined
      ? initialBundleParam
      : {
          order_details: createOrderDetails(initialPairing),
          points: initialPairing.points,
        };

  const [state, setState] = useState<UseEditRecipeState>({
    bundle: initialBundle,
    pairing: initialPairing,
    mutations: [],
  });

  const { pairing, bundle, mutations } = state;

  const swappableRecipes = useSwappableRecipesQuery(
    {
      inventoryDate,
      orderId: orderId === undefined ? 0 : orderId,
      recipeId: pairing.id,
    },
    {
      enabled: orderId !== undefined,
    }
  );

  const orderDetailProductIds = bundle.order_details.map(
    ({ product }) => product
  );
  const pairingIngredientIds: number[] = [];
  pairing.ingredients.forEach(({ amount, id }) => {
    for (let i = 0; i < amount; i += 1) {
      pairingIngredientIds.push(id);
    }
  });

  const isEdited =
    isEqual(sortBy(orderDetailProductIds), sortBy(pairingIngredientIds)) ===
    false;

  const orderDetailsIngredientsMap: Record<string, number> =
    bundle.order_details.reduce(
      (seed, { product }) => {
        if (seed[product] === undefined) {
          seed[product] = 0;
        }

        seed[product] += 1;
        return seed;
      },
      {} as Record<string, number>
    );
  const pairingIngredientsMap: Record<string, number> =
    pairing.ingredients.reduce(
      (seed, { id }) => {
        if (seed[id] === undefined) {
          seed[id] = 0;
        }

        seed[id] += 1;
        return seed;
      },
      {} as Record<string, number>
    );
  const uniqueIngredientsLength = Object.keys(
    orderDetailsIngredientsMap
  ).length;
  const hasIngredientsToAdd =
    Object.keys(pairingIngredientsMap).length !== uniqueIngredientsLength;

  const value: UseEditRecipeResult = {
    pairing,
    mutations,
    bundle,
    resetProductsToRemove: () => {
      setState({
        pairing,
        bundle: {
          order_details: createOrderDetails(pairing),
          points: pairing.points,
        },
        mutations: [],
      });
    },
    isEdited,
  };

  // if there are only 2 ingredients, do not allow any to be removed
  if (uniqueIngredientsLength > 2) {
    value.removeIngredient = (
      ingredientIdForRemoval: number,
      reasonId: number,
      onUpdate?: (mutations: PairingMutation[]) => void
    ) => {
      const orderDetails = bundle.order_details.filter(({ product }) => {
        return ingredientIdForRemoval !== product;
      });

      const nextMutation: RemoveIngredientMutation = {
        type: MutationTypes.RemoveIngredient,
        ingredientId: ingredientIdForRemoval,
        reasonId,
      };

      const nextState = produce(state, (draft) => {
        draft.bundle.order_details = orderDetails;
        draft.mutations.push(nextMutation);
      });

      setState(nextState);

      if (nextState.mutations.length && onUpdate) {
        onUpdate(nextState.mutations);
      }
    };
  }

  // if the original recipe and target recipe ingredients are not equal, it is implied that a modification has occurred, therefore an ingredient can be added back
  if (hasIngredientsToAdd === true) {
    value.addIngredient = (
      targetIngredientId: number,
      onUpdate?: (mutations: PairingMutation[]) => void
    ) => {
      const ingredient = pairing.ingredients.find(({ id: ingredientId }) => {
        return ingredientId === targetIngredientId;
      });
      const ingredientAmount = ingredient?.amount || 1;
      const orderDetails = [...bundle.order_details];

      for (let i = 0; i < ingredientAmount; i += 1) {
        orderDetails.push({
          product: targetIngredientId,
        });
      }

      const nextMutation: AddIngredientMutation = {
        type: MutationTypes.AddIngredient,
        ingredientId: targetIngredientId,
      };

      const nextState = produce(state, (draft) => {
        draft.bundle.order_details = orderDetails;
        draft.mutations.push(nextMutation);
      });

      setState(nextState);

      if (nextState.mutations.length && onUpdate) {
        onUpdate(nextState.mutations);
      }
    };
  }

  // if no modification has occurred, then an ingredient can still be swapped
  if (
    isEdited === false &&
    swappableRecipes.data !== undefined &&
    !isEmpty(swappableRecipes.data.swappable_pairings)
  ) {
    value.swap = {
      swapPairing: (
        pairing: Pairing,
        onUpdate?: (mutations: PairingMutation[], pairing: Pairing) => void
      ) => {
        const swapMutation: SwapMutation = {
          type: MutationTypes.Swap,
          pairingId: pairing.id,
        };
        const nextMutations: PairingMutation[] =
          initialBundleParam === undefined
            ? [swapMutation]
            : [...mutations, swapMutation];
        setState({
          pairing,
          bundle: {
            order_details: createOrderDetails(pairing),
            points: pairing.points,
          },
          mutations: nextMutations,
        });
        if (nextMutations.length && onUpdate) {
          onUpdate(nextMutations, pairing);
        }
      },
      pairings: swappableRecipes.data.swappable_pairings,
    };
  }

  return value;
}
